import React, { FC, ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Spinner } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import AppModal from '../../../components/app-modal/AppModal';
import i18next from 'i18next';
import { useAuthFetchFromRestAPI } from '../../../util/api';
import Image from 'react-bootstrap/Image';
import { formatDate, formatDateTime } from '../../../util/date-utils';
import { formatDuration } from '../../../util/duration-utils';
import { CallsByDayDto, CallsByPatientDto, TherapistDetailDto } from '../../../../../shared/dtos/therapist.dto';
import { TherapistBlockRequest } from '../../../../../shared/requests/therapistBlock.request';

type TherapistDetailsModalProps = {
  show: boolean;
  therapistId: string;
  onClose: () => void;
};

function getTherapistName(therapist: TherapistDetailDto): string {
  const fullName: string = `${therapist.firstName ?? ''} ${therapist.lastName ?? ''}`.trim();
  
  if (fullName) {
    return fullName;
  }
  
  return therapist.email ?? '-';
}

function renderInfoRow(label: string, value: ReactNode): ReactElement {
  return (
    <div className="d-flex align-items-start justify-content-between gap-3 py-2 border-bottom">
      <div className="text-muted">{label}</div>
      <div className="fw-semibold text-end text-break">{value}</div>
    </div>
  )
}

function renderEmptyTableRow(colSpan: number): ReactElement {
  return (
    <tr>
      <td colSpan={colSpan} className="text-center text-muted py-4">
        {i18next.t('therapistDetailsModal.noData')}
      </td>
    </tr>
  )
}


export const TherapistDetailsModal: FC<TherapistDetailsModalProps> = ({ show, therapistId, onClose }: TherapistDetailsModalProps): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();
  
  const [therapist, setTherapist] = useState<TherapistDetailDto | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [hasLoadError, setHasLoadError] = useState(false)
  const [hasActionError, setHasActionError] = useState(false)
  
  const fetchTherapistDetails = useCallback(async (): Promise<void> => {
    if (!therapistId) {
      return;
    }
    
    setIsLoading(true)
    setHasLoadError(false);
    setHasActionError(false);
    setTherapist(null);

    try {
      const response: TherapistDetailDto = await authFetch(`/api/v1/admin/therapists/${therapistId}`)
      setTherapist(response);
    } catch (error) {
      console.error(error)
      setHasLoadError(true);
    } finally {
      setIsLoading(false)
    }
  }, [authFetch, therapistId]);

  useEffect(() => {
    void fetchTherapistDetails();
  }, [fetchTherapistDetails]);
  
  async function handleBlockUnblock(): Promise<void> {
    if (!therapist || !therapistId) {
      return;
    }
    
    const shouldBlock: boolean = Boolean(therapist.approved);
    
    setIsSaving(true);
    setHasActionError(false);
    
    try {
      
      const request: TherapistBlockRequest = {
        therapistId,
        shouldBlock
      }
      
      await authFetch('/api/v1/admin/therapist/block', {
        method: 'POST',
        body: request
      });
      
      await fetchTherapistDetails();
    } catch (error) {
      console.error(error);
      setHasLoadError(true);
    } finally {
      setIsSaving(false);
    }
  }
  
  
  const isApproved: boolean = Boolean(therapist?.approved);
  
  return (
    <AppModal show={show} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{i18next.t('therapistDetailsModal.title')}</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {isLoading && (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" />
          </div>
        )}

        {!isLoading && hasLoadError && (
          <Alert variant="danger" className="mb-0">
            <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
              <span>{i18next.t('therapistDetailsModal.error')}</span>
              
              <Button type="button" variant="outline-danger" size="sm" onClick={fetchTherapistDetails}>
                {i18next.t('generic.retry')}
              </Button>
            </div>
          </Alert>
        )}

        {!isLoading && !hasLoadError && !therapist && (
          <div className="text-center text-muted py-5">
            {i18next.t('therapistDetailsModal.empty')}
          </div>
        )}

        {!isLoading && !hasLoadError && therapist && (
          <div className="d-flex flex-column gap-4">
            <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
              <div className="d-flex align-items-center gap-3">
                <Image
                  src={therapist.profilePic ?? '../default_profile.png'}
                  roundedCircle
                  className="border flex-shrink-0"
                  width={72}
                  height={72}
                  alt={getTherapistName(therapist)}
                />
                
                <div>
                  <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                    <h2 className="h4 mb-0">{getTherapistName(therapist)}</h2>

                    <Badge bg={isApproved ? 'success' : 'danger'}>
                      {isApproved ? i18next.t('therapistDetailsModal.active') : i18next.t('therapistDetailsModal.blocked')}
                    </Badge>
                  </div>
              
                  <div className="text-muted">{therapist.email ?? '-'}</div>
                </div>
              </div>
              
              <Button
                type="button"
                variant={isApproved ? 'outline-danger' : 'outline-success'}
                className="d-inline-flex align-items-center gap-2"
                disabled={isSaving}
                onClick={handleBlockUnblock}>
                {isSaving && (
                  <Spinner animation="border" size="sm" />
                )}

                {isApproved ? i18next.t('therapistDetailsModal.block') : i18next.t('therapistDetailsModal.unblock')}
              </Button>
            </div>

            {hasActionError && (
              <Alert variant="danger" className="mb-0">{i18next.t('therapistDetailsModal.actionError')}</Alert>
            )}
            
            <div className="row g-3">
              <div className="col-12 col-lg-6">
                <div className="card border-0 bg-light h-100">
                  <div className="card-body">
                    <h3 className="h6 mb-3">
                      {i18next.t('therapistDetailsModal.profileInfo')}
                    </h3>

                    {renderInfoRow(i18next.t('therapistDetailsModal.auth0Id'), therapist.accountId ?? '-')}
                    {renderInfoRow(i18next.t('therapistDetailsModal.onSchedResourceId'), therapist.onSchedResourceId ?? '-')}
                    {renderInfoRow(i18next.t('therapistDetailsModal.lastLoginAt'), formatDateTime(therapist.lastLoginAt))}
                  </div>
                </div>
              </div>
              
              <div className="col-12 col-lg-6">
                <div className="card border-0 bg-light h-100">
                  <div className="card-body">
                    <h3 className="h6 mb-3">
                      {i18next.t('therapistDetailsModal.personalDetails')}
                    </h3>

                    {renderInfoRow(i18next.t('therapistDetailsModal.firstName'), therapist.firstName ?? '-')}
                    {renderInfoRow(i18next.t('therapistDetailsModal.lastName'), therapist.lastName ?? '-')}
                    {renderInfoRow(i18next.t('therapistDetailsModal.email'), therapist.email ?? '-')}
                    {renderInfoRow(i18next.t('therapistDetailsModal.birthDate'), formatDate(therapist.birthDate))}
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex flex-column gap-3">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white fw-semibold">
                  {i18next.t('therapistDetailsModal.sessionsByDate')}
                </div>

                <div className="table-responsive">
                  <Table
                    hover
                    className="align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{i18next.t('therapistDetailsModal.day')}</th>
                        <th className="text-end">
                          {i18next.t('therapistDetailsModal.durationInHours')}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {(!therapist.callsByDay || therapist.callsByDay.length === 0) && renderEmptyTableRow(2)}

                      {therapist.callsByDay?.map((dto: CallsByDayDto) => (
                        <tr key={dto.day}>
                          <td>{formatDate(dto.day)}</td>
                          <td className="text-end">{formatDuration(dto.duration)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>

              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white fw-semibold">
                  {i18next.t('therapistDetailsModal.sessionsByPatient')}
                </div>

                <div className="table-responsive">
                  <Table
                    hover
                    className="align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{i18next.t('therapistDetailsModal.patientId')}</th>
                        <th>{i18next.t('therapistDetailsModal.patient')}</th>
                        <th className="text-end">
                          {i18next.t('therapistDetailsModal.durationInHours')}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {(!therapist.callsByPatient || therapist.callsByPatient.length === 0) && renderEmptyTableRow(3)}

                      {therapist.callsByPatient?.map((dto: CallsByPatientDto) => (
                        <tr key={dto.patientId}>
                          <td className="text-muted small">{dto.patientId === 'unknown' ? '-' : dto.patientId}</td>
                          <td>{dto.patientId === 'unknown' ? i18next.t('therapistDetailsModal.unknownPatient') : dto.patientName}</td>
                          <td className="text-end">{formatDuration(dto.duration)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button
          type="button"
          variant="secondary"
          onClick={onClose}>
          {i18next.t('generic.close')}
        </Button>
      </Modal.Footer>
    </AppModal>
  )
};

export default TherapistDetailsModal;
